import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Estimate.css';
import WhatsAppFloat from '../WhatsAppFloat';
import { getQuote, clearQuote } from '../../utils/session';

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const formatDuration = (minutes) => {
  const m = Number(minutes || 0);
  if (!m) return '';
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h > 0 ? `${h} hr${h > 1 ? 's' : ''} ` : ''}${r} min`;
};

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (raw) => {
  if (!raw) return '';
  // Accepts "HH:mm" or ISO
  if (/^\d{2}:\d{2}$/.test(raw)) {
    const [h, m] = raw.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const secondsUntil = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
};

const formatMMSS = (s) => {
  if (s == null) return '';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const Estimate = () => {
  const navigate = useNavigate();
  const [quote] = useState(() => getQuote());
  const [remaining, setRemaining] = useState(() => secondsUntil(quote?.validUntil));
  const [openAcc, setOpenAcc] = useState('rules');
  const [coupon, setCoupon] = useState('');

  useEffect(() => {
    if (!quote) {
      navigate('/', { replace: true });
    }
  }, [quote, navigate]);

  useEffect(() => {
    if (!quote?.validUntil) return undefined;
    const id = setInterval(() => setRemaining(secondsUntil(quote.validUntil)), 1000);
    return () => clearInterval(id);
  }, [quote?.validUntil]);

  if (!quote) return null;

  const trip = quote.trip || {};
  const fare = quote.fare || {};
  const origin = trip.origin || {};
  const destination = trip.destination || {};
  const vehicleLabel = trip.vehicle?.label || trip.vehicleType || quote.vehicleType || '—';
  const ratePerKm = trip.vehicle?.ratePerKm ?? fare.ratePerKm;
  const distanceKm = trip.distanceKm;
  const durationMin = trip.durationMin ?? trip.durationMins;
  const total = fare.total ?? 0;
  const baseFare = fare.baseFare ?? 0;
  const toll = fare.tollEstimate ?? fare.tollAmount ?? 0;
  const advancePercent = fare.advancePercent ?? 20;
  const advanceAmount = fare.advanceAmount ?? Math.round((total * advancePercent) / 100);
  const balanceAmount = fare.balanceAmount ?? Math.max(0, total - advanceAmount);

  const expired = quote?.validUntil ? remaining === 0 : false;

  const handleProceed = () => navigate('/confirm');

  const handleNewEstimate = () => {
    clearQuote();
    navigate('/', { replace: true });
  };

  const toggleAcc = (k) => setOpenAcc((p) => (p === k ? null : k));

  return (
    <div className="estimate-page">
      <div className="est-breadcrumb">
        <span className="bc-link" onClick={() => navigate('/')}>Home</span>
        <span className="bc-sep">›</span>
        <span>Book a ride</span>
        <span className="bc-sep">›</span>
        <span className="bc-current">Estimated Fare</span>
      </div>

      <div className="est-main">
        {/* LEFT PANEL */}
        <div className="est-left">
          <div className="est-route-card">
            <div className="est-route-eyebrow">Your route</div>
            <div className="est-route-row">
              <div className="est-route-city">{origin.address || '—'}</div>
              <div className="est-route-divider" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12,5 19,12 12,19" />
                </svg>
              </div>
              <div className="est-route-city dim">{destination.address || '—'}</div>
            </div>
            <div className="est-trip-chips">
              <span className="est-chip">
                <strong>{formatDate(trip.travelDate)}</strong>
                {trip.pickupTime ? ` · ${formatTime(trip.pickupTime)}` : ''}
              </span>
              <span className="est-chip">Vehicle: <strong>{vehicleLabel}</strong></span>
              <span className="est-chip">One-way taxi</span>
            </div>
            <div className="est-dist-row">
              <div className="est-dist-dot" />
              <div className="est-dist-text">
                About <strong>{distanceKm} KM</strong>
                {durationMin ? <> · <strong>{formatDuration(durationMin)}</strong></> : null}
              </div>
            </div>
          </div>

          <div className="est-info-card">
            <h4>Before you confirm</h4>
            <ul className="est-info-list">
              <li><span className="il-dot" />You pay only for this side — no return fare charged</li>
              <li><span className="il-dot" />{advancePercent}% advance required to confirm the booking</li>
              <li><span className="il-dot" />Patna pickup: min 4 hrs advance · Other cities: 12 hrs advance</li>
              <li><span className="il-dot" />Toll, parking and state fees are charged at actuals</li>
            </ul>
          </div>
        </div>

        {/* RIGHT PANEL — Fare card */}
        <div className="est-fare-card">
          <div className="fc-header">
            <h2>Estimated Fare</h2>
            <p>Fare confirmed — hold this quote for a few minutes.</p>
          </div>

          {quote.validUntil && (
            expired ? (
              <div className="fc-expired" style={{
                margin: '0 0 16px', padding: '12px 14px', borderRadius: 10,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)',
                color: '#B91C1C', fontFamily: "'DM Sans', sans-serif", fontSize: 14
              }}>
                ⏱ Quote expired. Please get a new estimate.
              </div>
            ) : (
              <div className="fc-timer" style={{
                margin: '0 0 16px', padding: '10px 14px', borderRadius: 10,
                background: 'rgba(232,89,10,0.06)', border: '1px solid rgba(232,89,10,0.25)',
                color: '#E8590A', fontFamily: "'DM Sans', sans-serif", fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 8
              }}>
                <span>⏱ Quote valid for</span>
                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMMSS(remaining)}</strong>
              </div>
            )
          )}

          <div className="breakdown">
            <div className="fc-label" style={{ marginBottom: 10 }}>{vehicleLabel} — fare breakdown</div>
            <div className="bd-rows">
              <div className="bd-row">
                <div>
                  <span className="bd-key">Base fare</span>
                  {ratePerKm ? (
                    <span className="bd-note">{distanceKm} km × ₹{ratePerKm}/km</span>
                  ) : (
                    <span className="bd-note">{distanceKm} km one-way</span>
                  )}
                </div>
                <span className="bd-val">{formatINR(baseFare)}</span>
              </div>
              <div className="bd-row">
                <div>
                  <span className="bd-key">Return fare</span>
                  <span className="bd-note">One-way — not charged</span>
                </div>
                <span className="bd-val green">₹0</span>
              </div>
              <div className="bd-row">
                <div>
                  <span className="bd-key">GST</span>
                  <span className="bd-note">Included in fare</span>
                </div>
                <span className="bd-val green">Included</span>
              </div>
              <div className="bd-row">
                <div>
                  <span className="bd-key">Toll / parking</span>
                  <span className="bd-note">{toll > 0 ? 'Estimate' : 'Charged at actuals'}</span>
                </div>
                <span className="bd-val amber">{toll > 0 ? formatINR(toll) : 'Extra'}</span>
              </div>
            </div>
            <div className="total-strip">
              <span className="ts-label">Estimated total</span>
              <span className="ts-amount">{formatINR(total)}</span>
            </div>

            <div className="bd-rows" style={{ marginTop: 12, borderTop: '1px dashed rgba(0,0,0,0.1)', paddingTop: 12 }}>
              <div className="bd-row">
                <div><span className="bd-key">Advance ({advancePercent}%)</span></div>
                <span className="bd-val" style={{ fontWeight: 700 }}>{formatINR(advanceAmount)}</span>
              </div>
              <div className="bd-row">
                <div><span className="bd-key">Balance on arrival</span></div>
                <span className="bd-val">{formatINR(balanceAmount)}</span>
              </div>
            </div>
          </div>

          <div className="coupon-wrap">
            <input
              className="coupon-input"
              type="text"
              placeholder="Enter coupon code"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
            />
            <button type="button" className="coupon-btn">Apply Now</button>
          </div>

          <div className="accordions">
            <Accordion title="Rules & Restrictions" open={openAcc === 'rules'} onToggle={() => toggleAcc('rules')}>
              <ul>
                <li>Excludes toll tax, parking fee, state fee</li>
                <li>GST is included in this estimated fare</li>
                {ratePerKm && <li>Extra KM charge ₹{ratePerKm}/km · Extra time ₹100/hr</li>}
                <li>{advancePercent}% advance payment required to confirm ride</li>
                <li>Patna: book 4 hrs advance · Others: 12 hrs advance</li>
              </ul>
            </Accordion>
            <Accordion title="Ride Inclusions" open={openAcc === 'inclusions'} onToggle={() => toggleAcc('inclusions')}>
              <ul>
                <li>Base fare for {distanceKm} km one-way</li>
                <li>Driver allowance for the trip</li>
                <li>Fuel and vehicle running costs</li>
                <li>GST as applicable</li>
              </ul>
            </Accordion>
            <Accordion title="Cancellation Policy" open={openAcc === 'cancel'} onToggle={() => toggleAcc('cancel')}>
              <ul>
                <li>Free cancellation up to 2 hours before pickup</li>
                <li>50% refund within 2 hours of pickup time</li>
                <li>No refund for no-shows at pickup point</li>
              </ul>
            </Accordion>
          </div>

          <div className="trust-wrap">
            <span className="ti"><span className="ti-dot" />GPS tracked</span>
            <span className="ti"><span className="ti-dot" />Verified driver</span>
            <span className="ti"><span className="ti-dot" />No hidden charges</span>
            <span className="ti"><span className="ti-dot" />Cash on arrival</span>
          </div>

          <div className="cta-row">
            <button type="button" className="btn-back" onClick={() => navigate('/')}>
              ← Back
            </button>
            {expired ? (
              <button type="button" className="btn-proceed" onClick={handleNewEstimate}>
                Get a new estimate →
              </button>
            ) : (
              <button type="button" className="btn-proceed" onClick={handleProceed}>
                Confirm this booking →
              </button>
            )}
          </div>
        </div>
      </div>

      <WhatsAppFloat />
    </div>
  );
};

const Accordion = ({ title, open, onToggle, children }) => (
  <div className={`acc${open ? ' open' : ''}`}>
    <div className="acc-h" onClick={onToggle} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}>
      {title}
      <svg viewBox="0 0 24 24" style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
        <polyline points="6,9 12,15 18,9" />
      </svg>
    </div>
    {open && <div className="acc-b">{children}</div>}
  </div>
);

export default Estimate;
